import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import MomentCore from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import parseISO from 'date-fns/parseISO';
import {
	DateRangePicker as RangePicker
} from "@matharumanpreet00/react-daterange-picker";

import {
	makeStyles,
	Menu,
	Button,
	ListItemText,
	Divider,
	Grid,
	Typography,
	useTheme
} from '@material-ui/core';

import { isJsonEqual } from "../utils/Json";
import Constants from "../utils/Constants";
import { usePrevious } from "../utils/CustomHooks";

const styles = makeStyles(theme => ({
	button: {
		border: theme.common.border,
		borderRadius: 12,
		paddingLeft: 16,
		paddingRight: 16
	},

	calendarIcon: {
		fontSize: theme.spacing(2)
	},

	downArrow: {
		paddingLeft: theme.spacing(.5)
	},

	menu: {
		"& button span.makeStyles-filled": {
			color: theme.palette.common.white
		}
	},

	menuList: {
		padding: 0,
		paddingTop: theme.spacing(1),

		"& > div:first-child": {
			boxShadow: "none"
		}
	},

	divider: {
		backgroundColor: theme.palette.grey[300]
	},

	footerGrid: {
		backgroundColor: theme.palette.common.white
	},

	actionButton: {
		margin: theme.spacing(1)
	},

	errorMsg: {
		paddingLeft: theme.spacing(2)
	}
}));

const dateFormat = "YYYY-MM-DD";
const today = parseISO((new MomentCore()).format(dateFormat));

// because functional components can't have refs and Menu uses refs for children internally(?)
const ForwardedRangePicker = React.forwardRef((props, ref) => (
	<RangePicker innerRef={ref} {...props} />
));
ForwardedRangePicker.displayName = "ForwardedRangePicker";

const DateRangePicker = React.memo(function DateRangePicker(props) {
	const menuRef = useRef();
	const [anchorEl, setAnchorEl] = useState();
	const [monthsAndYearsValid, setMonthsAndYearsValid] = useState(true);
	const [selectedDates, setSelectedDates] = useState({
		startDate: null,
		endDate: null,
		previousDays: null
	});

	let classes = styles(),
		{ className, updateHandler, initialDateRange, minDate, maxDate, definedRanges, usePickerDefaults } = props;

	const handleMenuClick = (e) => {
		setAnchorEl(e.currentTarget);
	};

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, [setAnchorEl]);

	const datesSelected = (range) => {
		let start = range.startDate ? (new MomentCore(range.startDate)) : null,
			end = range.endDate ? (new MomentCore(range.endDate)) : null,
			prev = end ? end.diff(start, "days") + 1 : null;

		setSelectedDates({
			previousDays: prev,
			startDate: start.format(dateFormat),
			endDate: end.format(dateFormat)
		});
	};

	const update = useCallback(() => {
		updateHandler(selectedDates);
	}, [selectedDates, updateHandler]);

	const getDisplayText = () => {
		let startDate,
			endDate,
			format = "MMM Do, YYYY",
			text = "No Range Selected...";

		if (selectedDates.startDate && selectedDates.endDate) {
			startDate = new MomentCore(selectedDates.startDate, dateFormat);
			endDate = new MomentCore(selectedDates.endDate, dateFormat);
			text = `${startDate.format(format)} - ${endDate.format(format)}`;
		}

		return text;
	};

	const selectsChanged = (monthsAndYearsValid) => {
		setMonthsAndYearsValid(monthsAndYearsValid);
	};

	// set initial date range
	useEffect(() => {
		if (initialDateRange) {
			datesSelected(initialDateRange);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	let prevDates = usePrevious(selectedDates);
	useEffect(() => {
		if (prevDates && !isJsonEqual(prevDates.selectedDates, selectedDates) && selectedDates.startDate && selectedDates.endDate) {
			update();
		}
	}, [selectedDates, update, prevDates]);

	const filteredRanges = definedRanges.filter(
		range => !props.excludeRanges.includes(range.label)
	);

	return (
		<div className={className}>
			<Button
				className={classes.button}
				aria-label="date range"
				onClick={e => handleMenuClick(e)}
				startIcon={<FontAwesomeIcon className={classes.calendarIcon} icon={["fad", "calendar-alt"]} />}>
				<ListItemText primary={getDisplayText()} />
				<FontAwesomeIcon className={classes.downArrow} icon={["far", "angle-down"]} />
			</Button>
			<Menu
				keepMounted
				ref={menuRef}
				onClose={handleClose}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				className={classes.menu}
				MenuListProps={{
					classes: {
						padding: classes.menuList
					}
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}>

				<ForwardedRangePicker
					open
					maxDate={parseISO(maxDate)}
					minDate={parseISO(minDate)}
					initialDateRange={{
						startDate: parseISO(initialDateRange.startDate),
						endDate: parseISO(initialDateRange.endDate)
					}}
					onChange={datesSelected}
					onSelectsChange={selectsChanged}
					definedRanges={usePickerDefaults ? undefined : filteredRanges}
					handleClose={handleClose} />
			</Menu>
		</div>
	);
}, (prevProps, newProps) => {
	// if they're the same, don't re-render (return true)
	return isJsonEqual(prevProps, newProps);
});

DateRangePicker.displayName = "DateRangePicker";

DateRangePicker.defaultProps = {
	usePickerDefaults: false, // this says to use definedRanges below (don't use the 3rd party components default ranges)
	excludeRanges: [],
	definedRanges: [{
		label: "Today",
		startDate: today,
		endDate: today
	}, {
		label: "7 Days",
		startDate: parseISO(((new MomentCore()).subtract(6, "days")).format(dateFormat)),
		endDate: today
	}, {
		label: "30 Days",
		startDate: parseISO(((new MomentCore()).subtract(29, "days")).format(dateFormat)),
		endDate: today
	}, {
		label: "2 Months",
		startDate: parseISO(((new MomentCore()).subtract(2, "months")).format(dateFormat)),
		endDate: today
	}, {
		label: "3 Months",
		startDate: parseISO(((new MomentCore()).subtract(3, "months")).format(dateFormat)),
		endDate: today
	}, {
		label: "6 Months",
		startDate: parseISO(((new MomentCore()).subtract(6, "months")).format(dateFormat)),
		endDate: today
	}, {
		label: "1 Year",
		startDate: parseISO(((new MomentCore()).subtract(1, "years")).format(dateFormat)),
		endDate: today
	}]
};

DateRangePicker.propTypes = {
	minDate: PropTypes.string,
	maxDate: PropTypes.string,
	definedRanges: PropTypes.array,
	excludeRanges: PropTypes.array,
	className: PropTypes.string,
	initialDateRange: PropTypes.object,
	usePickerDefaults: PropTypes.bool,
	updateHandler: PropTypes.func.isRequired
};

DateRangePicker.whyDidYouRender = Constants.ENV.IS_LOCAL;

export default DateRangePicker;